'use client';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { getSearchPagedData } from 'api/getSearchPagedData';
import { AdInsideList } from 'components/UI/AdInsideList/AdInsideList';
import SearchTeaserLoader from 'components/UI/SearchTeaserLoader/SearchTeaserLoader';
import { TeaserGridSection } from 'components/UI/TeaserGridSection';
import type { BasisPageData } from 'types/cms';
import type { WidgetTeaserStandardProps } from 'types/cms-teaser';
import { trackSearchLoadMoreClick } from 'utils/analytics';
import { slugify } from 'utils/string';
import styles from './search.module.scss';

export const WidgetTeaserSearch = ({ context }: { context: WidgetTeaserStandardProps }) => {
  const teaserOffset = context.trackingContext?.teaserOffset ?? 0;
  const sectionSlug = context.trackingContext?.sectionSlug ?? slugify(context.model?.fields?.title ?? 'search');
  const pagedAmount = context.items?.length ?? 0;
  const params = useSearchParams();
  const searchTerm = params.get('q');
  const type = context?.items?.[0]?.type;

  const [counter, setCounter] = useState<number>(0);
  const [filteredContext, setFilteredContext] = useState<WidgetTeaserStandardProps>(context);
  const [showLoadMore, setShowLoadMore] = useState<boolean>(type === 'storyline' || type === 'video');
  const [loading, setLoading] = useState<boolean>(false);

  const handleLoadMore = () => {
    setCounter((prev) => prev + 1);
    setLoading(true);
  };

  useEffect(() => {
    if (counter >= 1) {
      getSearchPagedData(`/suche/?q=${searchTerm}&page=${counter}`)
        .then((res: BasisPageData) => {
          const firstLevelSearchItems = res.areas.main
            ? res.areas.main.find((item) => item.view === 'search')?.items
            : [];
          const groups = res.areas.main ? res.areas.main.find((item) => item.type === 'group') : undefined;
          const groupSearchItems = groups?.children.find(
            (item: WidgetTeaserStandardProps) => item.view === 'search'
          )?.items;
          const searchItems = firstLevelSearchItems ?? groupSearchItems ?? undefined;
          
          if (searchItems?.length >= 1) {
            setFilteredContext((prev) => ({
              ...context,
              items: [...(prev.items ?? []), ...searchItems],
            }));
            trackSearchLoadMoreClick(searchTerm ?? '', counter, (searchItems?.length ?? 1) * counter);

            if (searchItems?.length < pagedAmount) {
              setShowLoadMore(false);
            }
          } else {
            setShowLoadMore(false);
          }
        })
        .then(() => {
          setLoading(false);
          window.MoltenBundle.cmd.push(() => {
            window.MoltenBundle.getClient().reloadAds();
          });
        })
        .catch((error) => {
          setLoading(false);
          setShowLoadMore(false);
          console.error('Error fetching search data:', error);
        });
    }
  }, [counter, searchTerm, context, pagedAmount]);

  return (
    <>
      <TeaserGridSection
        title={context.model?.fields?.title}
        titleLink={context.model?.fields?.link}
        titleAs="span"
        anchor={context.model?.fields?.anchor}
        variant="search"
      >
        <AdInsideList
          context={filteredContext}
          teaserOffset={teaserOffset}
          sectionSlug={sectionSlug}
          firstTeaserHighlight={context.model?.fields?.firstTeaserHighlight}
          variant="search"
        />
        {loading && <SearchTeaserLoader />}
      </TeaserGridSection>
      {showLoadMore && (
        <button aria-label="Mehr laden" className={styles.loadMore} onClick={handleLoadMore}>
          Mehr laden
        </button>
      )}
    </>
  );
};
